import { FC, useEffect, useMemo, useState } from "react";
import {
	Box,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { BasicDialog } from "../../../components/BasicDialog";
import {
	EducationalContentItem,
	EducationalContentType,
} from "../../../domain/program.interface";

type Props = {
	open: boolean;
	initialItem?: EducationalContentItem;
	onCancel: () => void;
	onSubmit: (item: EducationalContentItem) => void;
};

type FormState = {
	type: EducationalContentType;
	titleDef: string;
	descriptionDef: string;
	category: string;
	thumbnailUrl: string;
	contentUrl: string;
	duration: string;
	pageCount: string;
};

const emptyState: FormState = {
	type: EducationalContentType.VIDEO,
	titleDef: "",
	descriptionDef: "",
	category: "",
	thumbnailUrl: "",
	contentUrl: "",
	duration: "",
	pageCount: "",
};

const generateId = (): string => {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return `ec_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const itemToFormState = (item: EducationalContentItem): FormState => ({
	type: item.type,
	titleDef:
		(item.title as Record<string, string>)?.def ??
		Object.values(item.title ?? {})[0] ??
		"",
	descriptionDef:
		(item.description as Record<string, string>)?.def ??
		Object.values(item.description ?? {})[0] ??
		"",
	category: item.category ?? "",
	thumbnailUrl: item.thumbnailUrl ?? "",
	contentUrl: item.contentUrl ?? "",
	duration: item.duration ?? "",
	pageCount: typeof item.pageCount === "number" ? String(item.pageCount) : "",
});

export const EducationalContentItemDialog: FC<Props> = (props) => {
	const { open, initialItem, onCancel, onSubmit } = props;
	const [state, setState] = useState<FormState>(emptyState);

	useEffect(() => {
		if (!open) return;
		setState(initialItem ? itemToFormState(initialItem) : emptyState);
	}, [open, initialItem]);

	const isValid = useMemo(() => {
		if (!state.titleDef.trim()) return false;
		if (!state.contentUrl.trim()) return false;
		if (!state.category.trim()) return false;
		if (state.type === EducationalContentType.DOCUMENT && state.pageCount) {
			const n = Number(state.pageCount);
			if (Number.isNaN(n) || n < 1) return false;
		}
		return true;
	}, [state]);

	const handleSubmit = () => {
		if (!isValid) return;
		const built: EducationalContentItem = {
			id: initialItem?.id ?? generateId(),
			type: state.type,
			title: { def: state.titleDef.trim() },
			description: { def: state.descriptionDef.trim() },
			category: state.category.trim(),
			thumbnailUrl: state.thumbnailUrl.trim(),
			contentUrl: state.contentUrl.trim(),
			createdAt: initialItem?.createdAt ?? new Date().toISOString(),
		};
		if (state.type === EducationalContentType.VIDEO && state.duration.trim()) {
			built.duration = state.duration.trim();
		}
		if (state.type === EducationalContentType.DOCUMENT && state.pageCount) {
			built.pageCount = Number(state.pageCount);
		}
		onSubmit(built);
	};

	if (!open) return null;

	return (
		<BasicDialog
			title={
				initialItem ? "Edit Educational Content" : "Add Educational Content"
			}
			onCancel={onCancel}
			onSubmit={handleSubmit}
			submitText={initialItem ? "UPDATE" : "ADD"}
			sx={{
				"& .MuiDialog-paper": {
					width: "100%",
					maxWidth: 560,
				},
			}}
		>
			<Box sx={{ pt: 1 }}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth>
							<InputLabel id="ec-type-label">Type *</InputLabel>
							<Select
								labelId="ec-type-label"
								label="Type *"
								value={state.type}
								onChange={(e) =>
									setState((s) => ({
										...s,
										type: e.target.value as EducationalContentType,
									}))
								}
							>
								<MenuItem value={EducationalContentType.VIDEO}>Video</MenuItem>
								<MenuItem value={EducationalContentType.DOCUMENT}>
									Document
								</MenuItem>
								<MenuItem value={EducationalContentType.LINK}>Link</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							label="Category *"
							value={state.category}
							onChange={(e) =>
								setState((s) => ({ ...s, category: e.target.value }))
							}
							placeholder="e.g. APP INFORMATION"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Title *"
							value={state.titleDef}
							onChange={(e) =>
								setState((s) => ({ ...s, titleDef: e.target.value }))
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							multiline
							minRows={2}
							label="Description"
							value={state.descriptionDef}
							onChange={(e) =>
								setState((s) => ({ ...s, descriptionDef: e.target.value }))
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Thumbnail URL"
							value={state.thumbnailUrl}
							onChange={(e) =>
								setState((s) => ({ ...s, thumbnailUrl: e.target.value }))
							}
							placeholder="https://..."
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Content URL *"
							value={state.contentUrl}
							onChange={(e) =>
								setState((s) => ({ ...s, contentUrl: e.target.value }))
							}
							placeholder="https://..."
						/>
					</Grid>
					{state.type === EducationalContentType.VIDEO && (
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Duration"
								value={state.duration}
								onChange={(e) =>
									setState((s) => ({ ...s, duration: e.target.value }))
								}
								placeholder="mm:ss"
							/>
						</Grid>
					)}
					{state.type === EducationalContentType.DOCUMENT && (
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Page count"
								type="number"
								value={state.pageCount}
								onChange={(e) =>
									setState((s) => ({ ...s, pageCount: e.target.value }))
								}
								inputProps={{ min: 1 }}
							/>
						</Grid>
					)}
				</Grid>
			</Box>
		</BasicDialog>
	);
};
